/**
=========================================================
* Soft UI Dashboard PRO React - v2.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-material-ui
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// Core
import { DateTime } from "luxon";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";

// Utils
import { getDefaultPayload, extractPayload } from "pages/ManageContracts/Single/utils";

// Components
import ContractCard from "./ContractCard/index";

const today = DateTime.fromJSDate(new Date()).toFormat("yyyy-MM-dd");

function ContractForm({ contract, members, index, onContractChange }) {
  // --- General state ---
  const [buyType, setBuyType] = useState(contract?.buy_type || "NO_VALUES");
  const [sellType, setSellType] = useState(contract?.sell_type || "NO_VALUES");
  const [validity, setValidity] = useState(contract?.validity || today);
  const [voltageLevel, setVoltageLevel] = useState(contract?.voltage_level || "");
  const [tariffCycle, setTariffCycle] = useState(contract?.tariff_cycle || "");
  const [withHolidays, setWithHolidays] = useState(contract?.with_holidays || false);
  const [contractedPower, setContractedPower] = useState(contract?.contracted_power || 0);
  const [member, setMember] = useState(contract?.member || "");

  // --- Payload state (one per side) ---
  const [buyPayload, setBuyPayload] = useState(
    extractPayload(contract?.buy_payload, contract?.buy_type || "NO_VALUES")
  );
  const [sellPayload, setSellPayload] = useState(
    extractPayload(contract?.sell_payload, contract?.sell_type || "NO_VALUES")
  );

  // Handle GENERAL
  const onChangeBuyType = (e) => {
    setBuyType(e.value);
    setBuyPayload(getDefaultPayload(e.value));
  };
  const onChangeSellType = (e) => {
    setSellType(e.value);
    setSellPayload(getDefaultPayload(e.value));
  };
  const onChangeValidity = (e) => setValidity(DateTime.fromJSDate(e[0]).toFormat("yyyy-MM-dd"));
  const onChangeVoltageLevel = (e) => setVoltageLevel(e.value);
  const onChangeTariffCycle = (e) => setTariffCycle(e.value);
  const onChangeWithHolidays = (e) => setWithHolidays(e.target.checked);
  const onChangeContractedPower = (e, type) =>
    type === "select"
      ? setContractedPower(e.value)
      : setContractedPower(parseFloat(e.target.value));
  const onChangeMember = (e) => setMember(e.value);

  // Handle payloads
  const onChangeBuyPayload = (field, value) => {
    setBuyPayload((prev) => ({ ...prev, [field]: parseFloat(value) }));
  };
  const onChangeSellPayload = (field, value) => {
    setSellPayload((prev) => ({ ...prev, [field]: parseFloat(value) }));
  };

  // Build contract object and propagate on every change
  useEffect(() => {
    const newContract = {
      buy_type: buyType,
      sell_type: sellType,
      voltage_level: voltageLevel,
      tariff_cycle: tariffCycle,
      with_holidays: withHolidays,
      contracted_power: contractedPower,
      member,
      validity,
      buy_payload:
        buyType === "NO_VALUES" ? null : { ...buyPayload, buy_or_sell_type: "CONTRACT_BUY_VALUES" },
      sell_payload:
        sellType === "NO_VALUES"
          ? null
          : { ...sellPayload, buy_or_sell_type: "CONTRACT_SELL_VALUES" },
    };

    if (onContractChange) onContractChange(newContract);
  }, [
    buyType,
    sellType,
    buyPayload,
    sellPayload,
    voltageLevel,
    tariffCycle,
    withHolidays,
    contractedPower,
    validity,
    member,
  ]);

  // Build display contract
  const displayContract = {
    buy_type: buyType,
    sell_type: sellType,
    validity,
    voltage_level: voltageLevel,
    tariff_cycle: tariffCycle,
    with_holidays: withHolidays,
    contracted_power: contractedPower,
    member,
    buy_payload: buyPayload,
    sell_payload: sellPayload,
  };

  return (
    <ContractCard
      contract={displayContract}
      buyType={buyType}
      sellType={sellType}
      members={members}
      index={index}
      onChangeMember={onChangeMember}
      onChangeValidity={onChangeValidity}
      onChangeBuyType={onChangeBuyType}
      onChangeSellType={onChangeSellType}
      onChangeVoltageLevel={onChangeVoltageLevel}
      voltageLevel={voltageLevel}
      onChangeTariffCycle={onChangeTariffCycle}
      onChangeWithHolidays={onChangeWithHolidays}
      onChangeContractedPower={onChangeContractedPower}
      contractedPower={contractedPower}
      buyPayload={buyPayload}
      sellPayload={sellPayload}
      onChangeBuyPayload={onChangeBuyPayload}
      onChangeSellPayload={onChangeSellPayload}
    />
  );
}

ContractForm.defaultProps = {
  contract: null,
  index: undefined,
  onContractChange: undefined,
};

ContractForm.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  members: PropTypes.array.isRequired,
  onContractChange: PropTypes.func,
  index: PropTypes.number,
  // eslint-disable-next-line react/forbid-prop-types
  contract: PropTypes.object,
};

export default ContractForm;
